/*
 * TAAR — Pre-launch storefront engine
 * Renders the catalog, product detail, cart and pre-order flow from an editable
 * data file (assets/products.json). Cart persists in localStorage. No backend —
 * "buy" captures a pre-order / reservation.
 *
 * Config is provided by the host page on `window.PRELAUNCH`:
 *   { productsUrl, assetBase, currency, brand }
 * Falls back to a local ./assets/ layout so preview.html works with no server.
 */
(function () {
  'use strict';

  var CFG = window.PRELAUNCH || {};
  var PRODUCTS_URL = CFG.productsUrl || 'assets/products.json';
  var ASSET_BASE   = CFG.assetBase   || 'assets/';
  var STORAGE_KEY  = 'taar-prelaunch-cart';
  var LIKES_KEY    = 'taar-prelaunch-likes';   // wishlist — stored separately from the cart
  var CART_EXP_KEY = 'taar-prelaunch-cart-expiry';   // cart hold deadline (ms epoch)
  var CART_HOLD_MS = 15 * 60 * 1000;           // reserve the cart for 15 minutes
  var PDP          = CFG.pdp || {};            // product-page copy/toggles from sections/catalog.liquid (Shopify theme editor)
  var SHOP_DOMAIN  = CFG.shopDomain || 'mytaar.com';  // primary store domain — for off-Shopify preview permalinks
  // Storefront API — live stock check (public token, safe to embed; read-only).
  var SF_TOKEN     = CFG.storefrontToken   || 'ccefd0d6da922282522c6d33ef4e9117';
  var SF_DOMAIN    = CFG.storefrontDomain  || 'taar-8996.myshopify.com';
  var SF_VERSION   = CFG.storefrontVersion || '2026-07';
  var pdpGalleryTimer = null;                  // PDP image auto-advance interval (cleared on every route change)

  var state = {
    products: [],
    categories: [],
    comingSoon: [],   // post-launch weaves — teaser pills only, no products
    currency: CFG.currency || '₹',
    cart: loadCart(),
    likes: loadLikes(),
    filter: 'all'
  };

  /* ── Helpers ─────────────────────────────────────────────────────────────── */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function assetUrl(name) {
    if (!name) return '';
    if (/^(https?:)?\/\//.test(name) || name.charAt(0) === '/') return name;
    return ASSET_BASE + name;
  }
  function money(amount) {
    var n = Number(amount) || 0;
    return state.currency + n.toLocaleString('en-IN');
  }
  function loadCart() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch (e) { return []; }
  }
  function saveCart() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state.cart)); } catch (e) {}
  }
  function loadLikes() {
    try { return JSON.parse(localStorage.getItem(LIKES_KEY)) || []; } catch (e) { return []; }
  }
  function saveLikes() {
    try { localStorage.setItem(LIKES_KEY, JSON.stringify(state.likes)); } catch (e) {}
  }
  function findProduct(handle) {
    for (var i = 0; i < state.products.length; i++) {
      if (state.products[i].handle === handle) return state.products[i];
    }
    return null;
  }
  // Product-page copy/flags — fall back to the built-in defaults when a setting is
  // blank or the host page didn't supply PRELAUNCH.pdp (e.g. the local preview.html).
  function pdpText(key, fallback) {
    var v = PDP[key];
    return (v === undefined || v === null || v === '') ? fallback : v;
  }
  function pdpFlag(key, fallback) {
    var v = PDP[key];
    return (v === undefined || v === null) ? fallback : !!v;
  }
  // Product description → rich HTML. Descriptions in products.json come straight
  // from the product DB, so they may already carry markup (paragraphs, <strong>
  // labels, headings). Render that as-is. Plain-text descriptions are split on
  // blank lines into paragraphs so long copy isn't jammed into one block.
  function descHTML(text) {
    if (!text) return '';
    var s = String(text).trim();
    if (/<\/?[a-z][\s\S]*>/i.test(s)) return s;            // already HTML — trust it
    return s.split(/\n{2,}/).map(function (para) {
      return '<p>' + esc(para).replace(/\n/g, '<br>') + '</p>';
    }).join('');
  }

  /* ── Data ────────────────────────────────────────────────────────────────── */
  function loadProducts() {
    return fetch(PRODUCTS_URL, { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        state.products = (data && data.products) || [];
        state.categories = (data && data.categories) || [];
        state.comingSoon = (data && data.coming_soon) || [];
        if (data && data.currency_symbol) state.currency = data.currency_symbol;
      })
      .catch(function (err) {
        console.error('[TAAR pre-launch] Could not load products:', err);
        var host = $('[data-prelaunch="catalog"]');
        if (!host) return;
        if (location.protocol === 'file:') {
          host.innerHTML = '<p style="color:var(--color-text-muted)">Preview must be served over http — open <code>preview.html</code> with the VS Code “Live Server” extension (right-click → Open with Live Server), not by double-clicking.</p>';
        } else {
          host.innerHTML = '<p style="color:var(--color-text-muted)">Catalogue unavailable. Check assets/products.json.</p>';
        }
      });
  }

  /* ── Live availability (Storefront API) ──────────────────────────────────────
     Products render from the static products.json snapshot, so a piece that has
     sold in Shopify would still show as buyable until we re-check. This polls the
     Storefront API for each variant's live `availableForSale` and flips the
     product's `available` flag — the catalog/PDP already render a "Sold" + notify
     state from that flag. Shopify still guarantees no oversell at checkout; this
     is purely so the storefront reflects reality before the buyer clicks. */
  function refreshAvailability() {
    if (!SF_TOKEN) return Promise.resolve();
    var withVar = state.products.filter(function (p) { return p.variant_id; });
    if (!withVar.length) return Promise.resolve();
    var ids = withVar.map(function (p) { return 'gid://shopify/ProductVariant/' + p.variant_id; });
    var query = 'query($ids:[ID!]!){nodes(ids:$ids){... on ProductVariant{id availableForSale}}}';
    return fetch('https://' + SF_DOMAIN + '/api/' + SF_VERSION + '/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': SF_TOKEN },
      body: JSON.stringify({ query: query, variables: { ids: ids } })
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (!j || !j.data || !j.data.nodes) return;
        var avail = {};
        j.data.nodes.forEach(function (n) {
          if (n && n.id) avail[String(n.id).split('/').pop()] = !!n.availableForSale;
        });
        var changed = {};
        state.products.forEach(function (p) {
          var key = String(p.variant_id);
          if (p.variant_id && Object.prototype.hasOwnProperty.call(avail, key) && p.available !== avail[key]) {
            p.available = avail[key];
            changed[p.handle] = true;
          }
        });
        if (Object.keys(changed).length) {
          renderCatalog();
          // If a now-changed product's page is open, re-render it too.
          var m = (location.hash || '').match(/^#\/product\/(.+)$/);
          if (m) { var h = decodeURIComponent(m[1]); if (changed[h]) renderProduct(h); }
        }
      })
      .catch(function () { /* network hiccup — keep last-known availability */ });
  }

  /* ── Catalog ─────────────────────────────────────────────────────────────── */
  function categories() {
    // Prefer the explicit list in products.json so empty categories still get a
    // pill; otherwise derive the list from whatever categories the products use.
    if (state.categories && state.categories.length) return state.categories.slice();
    var seen = {}, out = [];
    state.products.forEach(function (p) {
      if (p.category && !seen[p.category]) { seen[p.category] = 1; out.push(p.category); }
    });
    return out;
  }

  // Elegant fabric-swatch placeholder, used when a product has no photo yet.
  function swatchArt(p) {
    var colors = p.colors || [];
    var c1 = (colors[0] && colors[0].hex) || '#7B1F2E';
    var c2 = (colors[1] && colors[1].hex) || '#2b0f14';
    var label = p.weave || p.fabric || '';
    return '<div class="swatch-art" style="--c1:' + esc(c1) + ';--c2:' + esc(c2) + '">' +
      '<span class="swatch-art__mono">TAAR</span>' +
      (label ? '<span class="swatch-art__weave">' + esc(label) + '</span>' : '') +
    '</div>';
  }

  // Media block for a card/pdp: real photo if provided, else the swatch placeholder.
  function mediaHTML(p, link) {
    var imgs = p.images || [];
    var inner;
    if (imgs.length) {
      var hover = imgs[1] ? '<img class="card__img card__img--hover" src="' + assetUrl(imgs[1]) + '" alt="" aria-hidden="true" loading="lazy">' : '';
      inner = '<img class="card__img card__img--primary" src="' + assetUrl(imgs[0]) + '" alt="' + esc(p.title) + '" loading="lazy" width="600" height="750">' + hover;
    } else {
      inner = swatchArt(p);
    }
    return link ? '<a href="#/product/' + esc(p.handle) + '" aria-label="' + esc(p.title) + '">' + inner + '</a>' : inner;
  }

  // Combined PDP media list: photos first, then any product videos. A video entry
  // in products.json ("videos": [...]) is a Shopify Files / CDN URL — either a plain
  // string, or { "src": "…", "poster": "…" } to set a custom poster frame.
  function productMedia(p) {
    var media = (p.images || []).map(function (src) { return { type: 'image', src: src }; });
    (p.videos || []).forEach(function (v) {
      if (!v) return;
      if (typeof v === 'string') media.push({ type: 'video', src: v, poster: '' });
      else if (v.src) media.push({ type: 'video', src: v.src, poster: v.poster || '' });
    });
    return media;
  }
  // Play triangle overlaid on a video thumbnail so it reads as a clip, not a photo.
  function playGlyph() {
    return '<span class="pdp__thumb-play" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></span>';
  }

  // Heart glyph (mirrors snippets/icon.liquid 'heart' so cards and Liquid match).
  function heartSvg() {
    return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" ' +
      'stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>';
  }
  // Small padlock glyph for the post-launch teaser chips.
  function lockSvg() {
    return '<svg class="chip__lock" viewBox="0 0 24 24" width="12" height="12" fill="none" ' +
      'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<rect x="5" y="11" width="14" height="9" rx="1.6"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';
  }
  function likeBtnHTML(p) {
    var liked = isLiked(p.handle);
    return '<button class="like-btn' + (liked ? ' is-liked' : '') + '" type="button" ' +
      'data-like="' + esc(p.handle) + '" aria-pressed="' + (liked ? 'true' : 'false') + '" ' +
      'aria-label="' + (liked ? 'Remove from wishlist' : 'Add to wishlist') + '">' + heartSvg() + '</button>';
  }

  function cardHTML(p) {
    var badge = !p.available
      ? '<span class="card__badge badge badge--muted">Sold</span>'
      : (p.badge ? '<span class="card__badge badge">' + esc(p.badge) + '</span>' : '');
    var quick = p.available
      ? '<button class="btn btn--primary btn--sm btn--full" data-add="' + esc(p.handle) + '">Add to cart</button>'
      : '<button class="btn btn--outline btn--sm btn--full" data-notify="' + esc(p.handle) + '">Notify me</button>';

    return '' +
      '<article class="card' + (p.available ? '' : ' card--soldout') + '" data-handle="' + esc(p.handle) + '">' +
        '<div class="card__media">' +
          badge +
          likeBtnHTML(p) +
          mediaHTML(p, true) +
          '<div class="card__quick">' + quick + '</div>' +
        '</div>' +
        '<div class="card__info">' +
          '<p class="card__meta">' + esc(p.weave && p.region ? p.weave + ' · ' + p.region : (p.region || p.weave || p.fabric || '')) + '</p>' +
          '<h3 class="card__title"><a href="#/product/' + esc(p.handle) + '">' + esc(p.title) + '</a></h3>' +
          priceHTML(p) +
        '</div>' +
      '</article>';
  }

  function priceHTML(p) {
    return '<div class="price"><span class="price__amount">' + money(p.price) + '</span></div>';
  }

  function renderFilters() {
    var host = $('[data-prelaunch="filters"]');
    if (!host) return;
    var list = ['all'].concat(categories());
    var html = list.map(function (f) {
      var label = f === 'all' ? 'All' : f;
      return '<button class="chip' + (state.filter === f ? ' is-active' : '') + '" data-filter="' + esc(f) + '">' + esc(label) + '</button>';
    }).join('');

    // Post-launch weaves: teaser pills that filter nothing. Hover/focus (or tap
    // on touch) reveals an "Available at launch" note; no products live beneath.
    html += (state.comingSoon || []).map(function (c) {
      return '<button class="chip chip--soon" type="button" data-soon="' + esc(c) + '" ' +
        'aria-disabled="true" data-tip="Available at launch">' +
        '<span class="chip__soon-label">' + esc(c) + '</span>' + lockSvg() +
        '</button>';
    }).join('');

    host.innerHTML = html;
  }

  var byPriceDesc = function (a, b) { return (b.price || 0) - (a.price || 0); };

  // "All" shows EVERY saree in the DB, arranged as a price hierarchy across
  // categories: each category is ranked highest-price-first, then the ranks are
  // interleaved — so the opening set is the top piece of each category, the next
  // set their second-highest, and so on, until every piece has appeared.
  // Selecting a specific category shows all of its pieces, highest price first.
  function catalogItems() {
    if (state.filter !== 'all') {
      return state.products
        .filter(function (p) { return p.category === state.filter; })
        .sort(byPriceDesc);
    }

    // Bucket every product by category. Seed the order from the declared category
    // list (so the hierarchy stays curated/stable), then append any category a
    // product uses that isn't declared, so nothing in the DB is ever dropped.
    var groups = {}, order = [];
    var ensure = function (k) { if (!groups[k]) { groups[k] = []; order.push(k); } };
    categories().forEach(ensure);
    state.products.forEach(function (p) {
      var k = p.category || '—';
      ensure(k);
      groups[k].push(p);
    });
    order.forEach(function (k) { groups[k].sort(byPriceDesc); });

    // Interleave by rank: rank-0 of each category first, then rank-1, …
    var out = [];
    var depth = order.reduce(function (m, k) { return Math.max(m, groups[k].length); }, 0);
    for (var rank = 0; rank < depth; rank++) {
      order.forEach(function (k) {
        if (groups[k][rank]) out.push(groups[k][rank]);
      });
    }
    return out;
  }

  var catalogPainted = false;
  function renderCatalog() {
    var host = $('[data-prelaunch="catalog"]');
    if (!host) return;

    var html = catalogItems().map(cardHTML).join('') ||
      '<p style="color:var(--color-text-muted)">No pieces in this category yet.</p>';

    var reduce = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Paint the new markup and stagger each card's entrance.
    var paint = function () {
      host.innerHTML = html;
      if (!reduce) {
        $all('.card', host).forEach(function (card, i) {
          // Cap the delay so long collections don't crawl in.
          card.style.setProperty('--card-delay', Math.min(i, 8) * 45 + 'ms');
        });
      }
      host.classList.remove('is-switching');
      catalogPainted = true;
      if (catalogCar) catalogCar.reset();   // new cards painted → rewind + restart autoplay
    };

    // First render (or reduced motion): paint immediately, no cross-fade.
    if (reduce || !catalogPainted) { paint(); return; }

    // Switching categories: fade the current grid out, then swap + fade in.
    host.classList.add('is-switching');
    var done = false;
    var swap = function () { if (done) return; done = true; paint(); };
    host.addEventListener('transitionend', swap, { once: true });
    setTimeout(swap, 260); // fallback in case transitionend doesn't fire
  }

  /* ── Catalogue carousel ──────────────────────────────────────────────────────
   * Opt-in via the theme setting (data-catalog-carousel="true"). Auto-advances one
   * card at a time on the configured interval; the arrows step one card and loop.
   * Pauses on hover/focus, when the tab is hidden, when the catalogue is off-screen
   * (incl. the SPA hiding it on a product page), and under reduced-motion. */
  var catalogCar = null;   // controller, built once by initCatalogCarousel()
  function initCatalogCarousel() {
    var wrap = $('[data-catalog-carousel]');
    if (!wrap || wrap.getAttribute('data-catalog-carousel') !== 'true') return;
    var track = $('[data-prelaunch="catalog"]', wrap);
    if (!track) return;

    var autoplay = wrap.getAttribute('data-autoplay') === 'true';
    var interval = parseInt(wrap.getAttribute('data-interval'), 10) || 4000;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var prevBtn = $('[data-carousel-prev]', wrap);
    var nextBtn = $('[data-carousel-next]', wrap);

    var timer = null, hoverPaused = false, offscreen = false;

    function stepSize() {
      var card = track.querySelector('.card');
      if (!card) return track.clientWidth;
      var cs = getComputedStyle(track);
      var gap = parseFloat(cs.columnGap || cs.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    }
    function maxScroll() { return track.scrollWidth - track.clientWidth - 1; }
    function go(dir) {
      if (dir > 0 && track.scrollLeft >= maxScroll()) { track.scrollTo({ left: 0, behavior: 'smooth' }); }
      else if (dir < 0 && track.scrollLeft <= 0)      { track.scrollTo({ left: maxScroll(), behavior: 'smooth' }); }
      else { track.scrollBy({ left: dir * stepSize(), behavior: 'smooth' }); }
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function start() {
      stop();
      if (!autoplay || reduce || hoverPaused || offscreen || document.hidden) return;
      if (maxScroll() <= 0) return;   // everything already fits — nothing to advance
      timer = setInterval(function () { go(1); }, interval);
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { go(-1); start(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { go(1); start(); });

    wrap.addEventListener('mouseenter', function () { hoverPaused = true; stop(); });
    wrap.addEventListener('mouseleave', function () { hoverPaused = false; start(); });
    wrap.addEventListener('focusin',  function () { hoverPaused = true; stop(); });
    wrap.addEventListener('focusout', function () { hoverPaused = false; start(); });
    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else start(); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { offscreen = !e.isIntersecting; });
        if (offscreen) stop(); else start();
      }, { threshold: 0 }).observe(wrap);
    }

    catalogCar = {
      start: start,
      stop: stop,
      reset: function () { track.scrollTo({ left: 0, behavior: 'auto' }); start(); }
    };
    start();
  }

  // Suggestions shown under a product: same category first, then fill with the
  // rest of the catalogue, excluding the piece being viewed.
  function relatedProducts(p, n) {
    n = n || 4;
    var same = state.products.filter(function (o) { return o.handle !== p.handle && o.category === p.category; });
    var rest = state.products.filter(function (o) { return o.handle !== p.handle && o.category !== p.category; });
    return same.concat(rest).slice(0, n);
  }

  /* ── Product detail ──────────────────────────────────────────────────────── */
  function renderProduct(handle) {
    var host = $('[data-prelaunch="product"]');
    if (!host) return false;
    var p = findProduct(handle);
    if (!p) return false;

    var media = productMedia(p);
    var posterFor = (p.images && p.images[0]) || '';   // fallback poster for videos
    // Figure contents for a media item — a zoomable photo, or a video player.
    function stageHTML(item) {
      if (!item) return swatchArt(p);
      if (item.type === 'video') {
        var poster = item.poster || posterFor;
        return '<video class="pdp__video" data-pdp-video controls playsinline preload="metadata"' +
          (poster ? ' poster="' + assetUrl(poster) + '"' : '') + '>' +
          '<source src="' + assetUrl(item.src) + '"></video>';
      }
      return '<img data-pdp-main src="' + assetUrl(item.src) + '" alt="' + esc(p.title) + '">';
    }
    var thumbs = media.map(function (item, i) {
      var inner;
      if (item.type === 'video') {
        var poster = item.poster || posterFor;
        inner = (poster ? '<img src="' + assetUrl(poster) + '" alt="" loading="lazy">'
                        : '<span class="pdp__thumb-fallback"></span>') + playGlyph();
      } else {
        inner = '<img src="' + assetUrl(item.src) + '" alt="" loading="lazy">';
      }
      return '<button class="pdp__thumb' + (i === 0 ? ' is-active' : '') + '" data-thumb="' + i + '"' +
        (item.type === 'video' ? ' data-thumb-video' : '') + '>' + inner + '</button>';
    }).join('');
    var figureInner = media.length ? stageHTML(media[0]) : swatchArt(p);

    var specs = [
      ['Fabric', p.fabric], ['Weave', p.weave], ['Category', p.category], ['Region', p.region], ['Care', p.care]
    ].filter(function (a) { return a[1]; }).map(function (a) {
      return '<div class="pdp__spec"><dt>' + esc(a[0]) + '</dt><dd>' + esc(a[1]) + '</dd></div>';
    }).join('');

    var eyebrow = esc(p.weave && p.region ? p.weave + ' · ' + p.region
      : (p.weave || p.region || p.fabric || p.category || ''));

    var buy = p.available
      ? '<button class="btn btn--gold" data-buynow="' + esc(p.handle) + '">' + esc(pdpText('buy_now_label', 'Buy now')) + '</button>' +
        '<button class="btn btn--outline" data-add="' + esc(p.handle) + '">' + esc(pdpText('add_to_cart_label', 'Add to cart')) + '</button>'
      : '<button class="btn btn--outline" data-notify="' + esc(p.handle) + '">' + esc(pdpText('notify_label', 'Notify me at launch')) + '</button>';
    var likedNow = isLiked(p.handle);
    var saveBtn = '<button class="btn btn--ghost pdp__like' + (likedNow ? ' is-liked' : '') + '" type="button" ' +
      'data-like="' + esc(p.handle) + '" aria-pressed="' + (likedNow ? 'true' : 'false') + '">' +
      heartSvg() + '<span data-like-label>' + esc(likedNow ? pdpText('saved_label', 'Saved to wishlist') : pdpText('save_label', 'Save to wishlist')) + '</span></button>';

    var related = pdpFlag('show_related', true) ? relatedProducts(p, 4) : [];
    var suggestions = related.length
      ? '<section class="section pdp-related">' +
          '<div class="container">' +
            '<div class="catalog__head"><div>' +
              '<p class="eyebrow">' + esc(pdpText('related_eyebrow', 'More from the edit')) + '</p>' +
              '<h2 class="section-heading">' + esc(pdpText('related_heading', 'You may also like')) + '</h2>' +
            '</div></div>' +
            '<div class="product-grid">' + related.map(cardHTML).join('') + '</div>' +
          '</div>' +
        '</section>'
      : '';

    var galleryClass = media.length > 1 ? 'pdp__gallery' : 'pdp__gallery pdp__gallery--single';

    var arrowsOn = media.length > 1 && pdpFlag('gallery_arrows', true);
    var navHTML = arrowsOn
      ? '<button class="pdp__nav pdp__nav--prev" type="button" data-pdp-prev aria-label="Previous image">' +
          '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>' +
        '</button>' +
        '<button class="pdp__nav pdp__nav--next" type="button" data-pdp-next aria-label="Next image">' +
          '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>' +
        '</button>'
      : '';

    host.innerHTML = '' +
      '<div class="container pdp-page">' +
        '<a class="pdp__back" href="#/" aria-label="' + esc(pdpText('back_label', 'Back to catalogue')) + '">' +
          '<span class="pdp__back-icon" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
              'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>' +
          '</span>' +
          '<span class="pdp__back-label">' + esc(pdpText('back_label', 'Back to catalogue')) + '</span>' +
        '</a>' +
        '<nav class="pdp__crumbs" aria-label="Breadcrumb">' +
          '<a href="#/">' + esc(pdpText('breadcrumb_label', 'Catalogue')) + '</a>' +
          (p.category ? '<span class="pdp__crumbs-sep">/</span><span>' + esc(p.category) + '</span>' : '') +
        '</nav>' +
        '<div class="pdp">' +
          '<div class="' + galleryClass + '">' +
            '<div class="pdp__stage">' +
              (!p.available
                ? '<span class="pdp__stage-badge badge badge--muted">Sold</span>'
                : (p.badge ? '<span class="pdp__stage-badge badge">' + esc(p.badge) + '</span>' : '')) +
              '<figure class="pdp__figure">' + figureInner + '</figure>' +
              navHTML +
            '</div>' +
            (media.length > 1 ? '<div class="pdp__thumbs">' + thumbs + '</div>' : '') +
          '</div>' +
          '<div class="pdp__panel">' +
            '<div class="pdp__panel-inner">' +
              (eyebrow ? '<p class="pdp__eyebrow">' + eyebrow + '</p>' : '') +
              '<h1 class="pdp__title">' + esc(p.title) + '</h1>' +
              '<div class="pdp__price">' + priceHTML(p) + '</div>' +
              (p.description ? '<div class="pdp__desc-wrap" data-desc-wrap>' +
                  '<div class="pdp__desc rte">' + descHTML(p.description) + '</div>' +
                  '<button type="button" class="pdp__desc-toggle" data-desc-toggle aria-expanded="false">Read more</button>' +
                '</div>' : '') +
              '<div class="pdp__buy">' + buy + saveBtn + '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>' +
      suggestions;

    // stagger the entrance of the suggestion cards
    $all('.pdp-related .card', host).forEach(function (card, i) {
      card.style.setProperty('--card-delay', Math.min(i, 8) * 45 + 'ms');
    });

    // Gallery: clicking a thumb swaps the stage between photos and video players.
    var figure = $('.pdp__figure', host);
    var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var zoomOK = canHover && !reduceMotion && pdpFlag('show_zoom', true);

    // Zoom only applies while the stage shows a photo (videos keep their controls).
    function markZoomable(i) {
      var item = media[i];
      figure.classList.toggle('is-zoomable', !!(item && item.type === 'image' && zoomOK));
    }
    markZoomable(0);

    // Swap the stage to media[i]; used by thumbnails, arrows and auto-advance.
    var current = 0;
    var thumbBtns = $all('[data-thumb]', host);
    function showMedia(i) {
      if (i < 0) i = media.length - 1;
      if (i >= media.length) i = 0;
      current = i;
      var playing = $('[data-pdp-video]', figure);
      if (playing) { try { playing.pause(); } catch (e) {} }
      figure.innerHTML = stageHTML(media[i]);
      thumbBtns.forEach(function (t) {
        t.classList.toggle('is-active', +t.getAttribute('data-thumb') === i);
      });
      markZoomable(i);
      var vid = $('[data-pdp-video]', figure);
      if (vid) { try { vid.play(); } catch (e) {} }   // start a freshly-picked clip
    }

    thumbBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        showMedia(+btn.getAttribute('data-thumb'));
        restartAuto();
      });
    });

    // Side arrows — step through the media list, wrapping at the ends.
    var prevBtn = $('[data-pdp-prev]', host);
    var nextBtn = $('[data-pdp-next]', host);
    if (prevBtn) prevBtn.addEventListener('click', function () { showMedia(current - 1); restartAuto(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { showMedia(current + 1); restartAuto(); });

    // Auto-advance every N seconds (theme-editor setting). Paused while the
    // cursor is over the image or a video is the active media. Skipped for a
    // single image or when the visitor prefers reduced motion.
    var autoOn = media.length > 1 && pdpFlag('gallery_autoplay', true) && !reduceMotion;
    var intervalMs = Math.max(2, parseFloat(pdpText('gallery_interval', 4)) || 4) * 1000;
    var paused = false;
    function autoAllowed() {
      return autoOn && !paused && !(media[current] && media[current].type === 'video');
    }
    function startAuto() {
      if (!autoOn || pdpGalleryTimer) return;
      pdpGalleryTimer = setInterval(function () { if (autoAllowed()) showMedia(current + 1); }, intervalMs);
    }
    function stopAuto() { if (pdpGalleryTimer) { clearInterval(pdpGalleryTimer); pdpGalleryTimer = null; } }
    function restartAuto() { stopAuto(); startAuto(); }   // reset the clock after a manual pick

    var stage = $('.pdp__stage', host);
    if (stage && autoOn) {
      stage.addEventListener('pointerenter', function () { paused = true; });
      stage.addEventListener('pointerleave', function () { paused = false; });
    }
    startAuto();

    // Description "Read more" — collapsed to ~18 lines with a fade, expands on
    // click. The check for whether the toggle is even needed must run while the
    // panel is VISIBLE: route() adds `.is-open` to the product view only AFTER
    // renderProduct() returns, so measuring here directly would read 0 height on
    // a display:none panel and wrongly hide the toggle. Defer with rAF (retrying
    // a few frames) until the element has a real height, then decide.
    var descWrap = $('[data-desc-wrap]', host);
    if (descWrap) {
      var descEl = $('.pdp__desc', descWrap);
      var descBtn = $('[data-desc-toggle]', descWrap);

      if (descBtn) {
        descBtn.addEventListener('click', function () {
          // Anchor the button in the viewport across the toggle. Collapsing a long
          // description shrinks the page by many lines; without this the browser
          // clamps the scroll and snaps up to the top of the product (which feels
          // like being sent "back"). Keep the button where it sits on screen.
          var beforeTop = descBtn.getBoundingClientRect().top;
          var open = descWrap.classList.toggle('is-open');
          descBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
          descBtn.textContent = open ? 'Read less' : 'Read more';
          var afterTop = descBtn.getBoundingClientRect().top;   // forces reflow → new position
          window.scrollBy(0, afterTop - beforeTop);
        });
      }

      var descTries = 0;
      var syncDescToggle = function () {
        if (!descEl.clientHeight && descTries++ < 30) {   // panel still hidden — wait a frame
          requestAnimationFrame(syncDescToggle);
          return;
        }
        var overflowing = descEl.scrollHeight > descEl.clientHeight + 4;
        if (overflowing) {
          descWrap.classList.remove('is-open');           // keep collapsed, show the toggle
          if (descBtn) descBtn.hidden = false;
        } else {
          descWrap.classList.add('is-open');              // fits — reveal all, drop the toggle
          if (descBtn) descBtn.hidden = true;
        }
      };
      requestAnimationFrame(syncDescToggle);
    }

    // Cursor-follow zoom — magnify into wherever the mouse hovers on the photo.
    // Listeners live on the figure so they survive the innerHTML swaps above.
    if (figure && zoomOK) {
      var ZOOM = 2, raf = 0, px = 0.5, py = 0.5;
      var apply = function () {
        raf = 0;
        var img = $('[data-pdp-main]', figure);
        if (!img) return;
        // translate so the point under the cursor stays put while scaled up
        var tx = px * (1 - ZOOM) * 100;
        var ty = py * (1 - ZOOM) * 100;
        img.style.transform = 'translate(' + tx + '%, ' + ty + '%) scale(' + ZOOM + ')';
      };
      figure.addEventListener('mousemove', function (e) {
        if (!$('[data-pdp-main]', figure)) return;   // video stage: skip zoom
        var r = figure.getBoundingClientRect();
        px = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1);
        py = Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1);
        if (!raf) raf = requestAnimationFrame(apply);
      });
      figure.addEventListener('mouseleave', function () {
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
        var img = $('[data-pdp-main]', figure);
        if (img) img.style.transform = '';   // eases back to natural size
      });
    }
    return true;
  }

  /* ── Router (hash-based, works single-page & on Shopify) ─────────────────── */
  function route() {
    var hash = window.location.hash || '#/';
    // Stop any running PDP auto-advance; renderProduct restarts it when needed.
    if (pdpGalleryTimer) { clearInterval(pdpGalleryTimer); pdpGalleryTimer = null; }
    var m = hash.match(/^#\/product\/(.+)$/);
    var catalogView = $('[data-view="catalog"]');
    var productView = $('[data-view="product"]');
    var likedView   = $('[data-view="liked"]');

    if (m && productView) {
      var ok = renderProduct(decodeURIComponent(m[1]));
      if (ok) {
        if (catalogView) catalogView.classList.remove('is-open');
        if (likedView) likedView.classList.remove('is-open');
        productView.classList.add('is-open');
        document.body.setAttribute('data-route', 'product');
        window.scrollTo(0, 0);
        return;
      }
    }

    // Our Story — a dedicated in-theme page (Our Story + Meet our Weavers).
    // No overlay element to toggle: CSS shows the [js-story-view] sections and
    // hides the homepage ones off the body[data-route="story"] flag.
    if (hash === '#/story') {
      if (catalogView) catalogView.classList.remove('is-open');
      if (productView) productView.classList.remove('is-open');
      if (likedView) likedView.classList.remove('is-open');
      document.body.setAttribute('data-route', 'story');
      window.scrollTo(0, 0);
      return;
    }

    // Wishlist — a standalone page of saved pieces.
    if (hash === '#/liked' && likedView) {
      renderLiked();
      if (catalogView) catalogView.classList.remove('is-open');
      if (productView) productView.classList.remove('is-open');
      likedView.classList.add('is-open');
      document.body.setAttribute('data-route', 'liked');
      window.scrollTo(0, 0);
      return;
    }

    // default: catalog
    var wasProduct = document.body.getAttribute('data-route') === 'product';
    if (productView) productView.classList.remove('is-open');
    if (likedView) likedView.classList.remove('is-open');
    if (catalogView) catalogView.classList.add('is-open');
    document.body.setAttribute('data-route', 'catalog');

    // Scroll behaviour on the homepage route:
    //  • a section anchor in the hash (#catalogue, #craft, …) scrolls to that
    //    section — handling it here (rather than relying on the browser's native
    //    fragment jump) works even when the section was hidden under a different
    //    route an instant earlier, e.g. coming from the Our Story page;
    //  • otherwise, returning from a product lands back on the catalogue, and a
    //    first load stays on the hero.
    var reduce = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var anchorId = (hash && hash.charAt(1) !== '/' && hash !== '#') ? hash.slice(1) : '';
    var target = anchorId ? document.getElementById(anchorId) : null;
    if (target) {
      requestAnimationFrame(function () {
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      });
    } else if (wasProduct && catalogView) {
      requestAnimationFrame(function () {
        catalogView.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      });
    }
  }

  /* ── Wishlist (likes) — persisted separately from the cart ───────────────── */
  function isLiked(handle) { return state.likes.indexOf(handle) !== -1; }
  function toggleLike(handle) {
    var i = state.likes.indexOf(handle);
    var liked;
    if (i === -1) { state.likes.push(handle); liked = true; }
    else { state.likes.splice(i, 1); liked = false; }
    saveLikes();
    syncLikes();
    var p = findProduct(handle);
    toast(liked ? (esc(p ? p.title : 'Piece') + ' saved to wishlist') : 'Removed from wishlist');
    // If the wishlist is on screen, re-render so unliked pieces drop out.
    if (document.body.getAttribute('data-route') === 'liked') renderLiked();
  }
  // Reflect the current likes across every heart button + the header count.
  function syncLikes() {
    var count = state.likes.length;
    $all('[data-likes-count]').forEach(function (n) {
      n.textContent = count;
      n.setAttribute('data-count', count);
    });
    $all('[data-like]').forEach(function (btn) {
      var liked = isLiked(btn.getAttribute('data-like'));
      btn.classList.toggle('is-liked', liked);
      btn.setAttribute('aria-pressed', liked ? 'true' : 'false');
      btn.setAttribute('aria-label', liked ? 'Remove from wishlist' : 'Add to wishlist');
      var label = btn.querySelector('[data-like-label]');
      if (label) label.textContent = liked ? pdpText('saved_label', 'Saved to wishlist') : pdpText('save_label', 'Save to wishlist');
    });
  }
  function renderLiked() {
    var host = $('[data-prelaunch="liked"]');
    if (!host) return;
    var items = state.likes.map(findProduct).filter(Boolean);
    host.innerHTML = items.length
      ? items.map(cardHTML).join('')
      : '<p style="color:var(--color-text-muted)">No saved pieces yet. Tap the heart on any saree to keep it here.</p>';
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) {
      $all('.card', host).forEach(function (card, i) {
        card.style.setProperty('--card-delay', Math.min(i, 8) * 45 + 'ms');
      });
    }
    syncLikes();
  }

  /* ── Cart ────────────────────────────────────────────────────────────────── */
  function cartCount() {
    return state.cart.reduce(function (n, l) { return n + l.qty; }, 0);
  }
  function cartTotal() {
    return state.cart.reduce(function (sum, l) {
      var p = findProduct(l.handle);
      return sum + (p ? p.price * l.qty : 0);
    }, 0);
  }
  function addToCart(handle, silent) {
    var p = findProduct(handle);
    if (!p || !p.available) return;
    var line = state.cart.filter(function (l) { return l.handle === handle; })[0];
    /* Single-piece store: every saree is one-of-a-kind, so it can be in the
       cart at most once — adding again is a no-op, not a quantity bump. */
    if (line) {
      if (!silent) { toast(esc(p.title) + ' is already in your cart'); openDrawer(); }
      return;
    }
    state.cart.push({ handle: handle, qty: 1 });
    saveCart();
    syncCart();
    if (!silent) { toast(esc(p.title) + ' added'); openDrawer(); }
  }
  function removeLine(handle) {
    state.cart = state.cart.filter(function (l) { return l.handle !== handle; });
    saveCart();
    syncCart();
  }
  function syncCart() {
    manageCartTimer();
    $all('[data-cart-count]').forEach(function (n) {
      var c = cartCount();
      n.textContent = c;
      n.setAttribute('data-count', c);
    });
    renderCartBody();
  }

  /* ── Cart hold timer ─────────────────────────────────────────────────────────
     Adding the first piece starts a 15-minute reservation window (persisted, so
     it survives reloads). A live countdown shows in the cart drawer; when it hits
     zero the cart empties itself. Subsequent adds keep the original deadline —
     it's a fixed hold on the session, not reset on every add. Note: this is an
     urgency/UX device — real stock is still governed by Shopify at checkout. */
  var cartTimerId = null;
  function cartExpiry() {
    try { return parseInt(localStorage.getItem(CART_EXP_KEY), 10) || 0; } catch (e) { return 0; }
  }
  function setCartExpiry(ts) {
    try { if (ts) localStorage.setItem(CART_EXP_KEY, String(ts)); else localStorage.removeItem(CART_EXP_KEY); } catch (e) {}
  }
  function stopCartTimer() { if (cartTimerId) { clearInterval(cartTimerId); cartTimerId = null; } }
  function startCartTimer() {
    if (cartTimerId) return;
    cartTimerId = setInterval(function () {
      var exp = cartExpiry();
      if (!exp || !state.cart.length) { stopCartTimer(); return; }
      var rem = exp - Date.now();
      if (rem <= 0) { expireCart(); } else { renderCartTimer(rem); }
    }, 1000);
  }
  function expireCart() {
    stopCartTimer();
    setCartExpiry(0);
    state.cart = [];
    saveCart();
    toast('Your 15-minute cart hold expired');
    syncCart();
  }
  // Called from syncCart on every cart change (and on boot): starts, resumes,
  // renders, or clears the hold to match the current cart.
  function manageCartTimer() {
    if (!state.cart.length) { setCartExpiry(0); stopCartTimer(); renderCartTimer(0); return; }
    var exp = cartExpiry();
    if (!exp) { exp = Date.now() + CART_HOLD_MS; setCartExpiry(exp); }
    if (Date.now() >= exp) { expireCart(); return; }
    startCartTimer();
    renderCartTimer(exp - Date.now());
  }
  function renderCartTimer(rem) {
    var wrap = $('[data-cart-timer]');
    if (!wrap) return;
    if (!state.cart.length || rem <= 0) { wrap.hidden = true; return; }
    wrap.hidden = false;
    var totalSec = Math.max(0, Math.ceil(rem / 1000));
    var mm = Math.floor(totalSec / 60), ss = totalSec % 60;
    var clock = $('[data-cart-timer-clock]', wrap);
    if (clock) clock.textContent = mm + ':' + (ss < 10 ? '0' : '') + ss;
    var fill = $('[data-cart-timer-fill]', wrap);
    if (fill) fill.style.width = Math.max(0, Math.min(100, (rem / CART_HOLD_MS) * 100)) + '%';
    wrap.classList.toggle('is-urgent', rem <= 120000);   // final 2 minutes
  }
  function renderCartBody() {
    var body = $('[data-cart-body]');
    var foot = $('[data-cart-foot]');
    if (!body) return;
    if (!state.cart.length) {
      body.innerHTML = '<div class="cart-empty"><p>Your cart is empty.</p>' +
        '<p style="margin-top:var(--space-4)"><a class="link-underline" href="#/" data-drawer-close>Browse the collection</a></p></div>';
      if (foot) foot.hidden = true;
      return;
    }
    body.innerHTML = state.cart.map(function (l) {
      var p = findProduct(l.handle);
      if (!p) return '';
      return '<div class="line-item">' +
        '<div class="line-item__media"><img src="' + assetUrl((p.images || [])[0]) + '" alt=""></div>' +
        '<div>' +
          '<a class="line-item__title" href="#/product/' + esc(p.handle) + '" data-drawer-close>' + esc(p.title) + '</a>' +
          '<div class="line-item__meta">' + esc(p.weave || p.fabric || '') + '</div>' +
          '<div><button class="line-item__remove" data-remove="' + esc(p.handle) + '">Remove</button></div>' +
        '</div>' +
        '<div class="line-item__price">' + money(p.price) + '</div>' +
      '</div>';
    }).join('');
    if (foot) {
      foot.hidden = false;
      var val = $('[data-cart-total]', foot);
      if (val) val.textContent = money(cartTotal());
    }
  }

  function openDrawer() {
    $('[data-drawer]') && $('[data-drawer]').classList.add('is-open');
    $('[data-drawer-backdrop]') && $('[data-drawer-backdrop]').classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    $('[data-drawer]') && $('[data-drawer]').classList.remove('is-open');
    $('[data-drawer-backdrop]') && $('[data-drawer-backdrop]').classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* Register an email into the Shopify customer database in the background.
     Fires the same POST Shopify's {% form 'customer' %} makes (form_type=customer
     → /contact), so the address lands in Admin → Customers with email-marketing
     consent and the given tag. The response is ignored — the reservation's own
     inline confirmation is unaffected. No-ops off Shopify (e.g. the local file://
     preview, where /contact doesn't exist and the request is simply caught). */
  function registerCustomer(email, name, tags) {
    if (!email || location.protocol === 'file:') return;
    try {
      var body = new FormData();
      body.append('form_type', 'customer');
      body.append('utf8', '✓');
      body.append('contact[email]', email);
      if (name) body.append('contact[first_name]', name);
      if (tags) body.append('contact[tags]', tags);
      fetch('/contact', { method: 'POST', body: body, credentials: 'same-origin' })
        .catch(function () {});
    } catch (e) {}
  }

  /* ── Checkout (details → payment → confirmation) ─────────────────────────────
     Two-step purchase flow. Step 1 collects contact/delivery details, step 2 is
     the payment step. The actual charge is delegated to a pluggable gateway hook
     (see payNow) so the store owner can wire Razorpay / Stripe / etc. later
     without touching this flow. Until a gateway is configured the payment is
     simulated so the flow stays testable end-to-end. */
  var pendingOrder = null;   // built at the details step, charged at the payment step

  function openCheckout() {
    if (!state.cart.length) { toast('Your cart is empty'); return; }
    var modal = $('[data-modal]');
    if (!modal) return;
    renderCheckoutSummary();
    var form = $('[data-checkout-form]');
    if (form) form.reset();
    pendingOrder = null;
    showCheckoutStep('details');
    closeDrawer();
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }
  function closeCheckout() {
    var modal = $('[data-modal]');
    if (modal) modal.classList.remove('is-open');
    document.body.style.overflow = '';
  }
  // Show one checkout step ('details' | 'payment' | 'success'); hide the rest.
  function showCheckoutStep(name) {
    $all('[data-checkout-step]').forEach(function (s) {
      s.hidden = s.getAttribute('data-checkout-step') !== name;
    });
  }
  // Fill every summary block (details + payment steps share the same markup).
  function renderCheckoutSummary() {
    var rows = state.cart.map(function (l) {
      var p = findProduct(l.handle);
      if (!p) return '';
      return '<div class="modal__summary-row"><span>' + esc(p.title) + '</span><span>' + money(p.price) + '</span></div>';
    }).join('') +
    '<div class="modal__summary-total"><span>Total</span><span>' + money(cartTotal()) + '</span></div>';
    $all('[data-checkout-summary]').forEach(function (n) { n.innerHTML = rows; });
  }

  // Step 1 → build the order from the form, then move to the payment step.
  function submitCheckoutDetails(e) {
    e.preventDefault();
    var form = e.target;
    pendingOrder = {
      id: 'TAAR-' + Date.now().toString(36).toUpperCase(),
      placed_at: new Date().toISOString(),
      paid: false,
      customer: {
        name: form.name.value.trim(),
        email: form.email.value.trim(),
        phone: form.phone.value.trim(),
        address: form.address.value.trim()
      },
      items: state.cart.slice(),
      total: cartTotal(),
      currency: state.currency
    };
    var payBtn = $('[data-pay-button]');
    if (payBtn) { payBtn.disabled = false; payBtn.textContent = 'Pay ' + money(pendingOrder.total); }
    showCheckoutStep('payment');
  }

  // Step 2 → charge. Hands off to a real gateway when one is wired; otherwise
  // simulates a successful charge so the confirmation flow can be demoed.
  function payNow() {
    if (!pendingOrder) return;
    var payBtn = $('[data-pay-button]');

    // ── Payment gateway integration point ───────────────────────────────────
    // Wire your provider here. Define window.TAAR_PAY(order, onSuccess, onFailure)
    // — e.g. open Razorpay/Stripe checkout for `order.total` (in order.currency),
    // then call onSuccess(paymentInfo) once the charge clears, or onFailure(msg).
    if (typeof window.TAAR_PAY === 'function') {
      if (payBtn) { payBtn.disabled = true; payBtn.textContent = 'Processing…'; }
      window.TAAR_PAY(pendingOrder, onPaymentSuccess, onPaymentFailure);
      return;
    }

    // No gateway configured yet → simulate a successful payment.
    if (payBtn) { payBtn.disabled = true; payBtn.textContent = 'Processing…'; }
    setTimeout(function () { onPaymentSuccess({ simulated: true }); }, 900);
  }

  function onPaymentSuccess(payment) {
    var order = pendingOrder;
    if (!order) return;
    order.paid = true;
    order.paid_at = new Date().toISOString();
    order.payment = payment || {};

    // Persist the order locally (no backend in pre-launch) so it isn't lost.
    try {
      var log = JSON.parse(localStorage.getItem('taar-prelaunch-orders') || '[]');
      log.push(order);
      localStorage.setItem('taar-prelaunch-orders', JSON.stringify(log));
    } catch (err) {}

    // Register the buyer's email in the Shopify customer database.
    registerCustomer(order.customer.email, order.customer.name, 'order');

    // Clear the cart and show the confirmation.
    state.cart = [];
    saveCart();
    syncCart();

    var ref = $('[data-order-ref]');
    if (ref) ref.textContent = order.id;
    showCheckoutStep('success');
    pendingOrder = null;
  }

  function onPaymentFailure(msg) {
    var payBtn = $('[data-pay-button]');
    if (payBtn && pendingOrder) { payBtn.disabled = false; payBtn.textContent = 'Pay ' + money(pendingOrder.total); }
    toast(msg || 'Payment failed. Please try again.');
  }

  /* ── Shopify checkout (Option A: native hosted checkout + Shopify Payments) ───
     Hands the cart to Shopify's own checkout so the store's configured payment
     gateway takes the money and the order lands in Shopify Admin. On the live
     store we populate Shopify's cart via the AJAX API and jump straight to
     /checkout; off-Shopify (local preview.html) we fall back to an absolute
     cart permalink so testing still reaches the real checkout. */
  function isLocalPreview() {
    return location.protocol === 'file:' ||
      location.hostname === '' ||
      /^(localhost|127\.|0\.0\.0\.0)/.test(location.hostname);
  }
  function cartCheckoutLines() {
    return state.cart.map(function (l) {
      var p = findProduct(l.handle);
      return (p && p.variant_id) ? { variant_id: String(p.variant_id), qty: l.qty || 1 } : null;
    }).filter(Boolean);
  }
  function cartPermalink(lines, absolute) {
    var path = lines.map(function (l) { return l.variant_id + ':' + l.qty; }).join(',');
    return (absolute ? 'https://' + SHOP_DOMAIN : '') + '/cart/' + path;
  }
  // lines: optional [{variant_id, qty}] — defaults to the whole cart (e.g. "Buy now"
  // passes a single line so it checks out just that piece).
  function goToShopifyCheckout(lines) {
    lines = lines || cartCheckoutLines();
    if (!lines.length) { toast('Your cart is empty'); return; }

    if (isLocalPreview()) {                 // preview → real store checkout via permalink
      window.location.href = cartPermalink(lines, true);
      return;
    }

    toast('Taking you to secure checkout…');
    var items = lines.map(function (l) { return { id: Number(l.variant_id), quantity: l.qty }; });
    fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); window.location.href = '/checkout'; })
      .catch(function () { window.location.href = cartPermalink(lines, false); });   // fallback: cart permalink
  }

  /* ── Toast ───────────────────────────────────────────────────────────────── */
  var toastTimer;
  function toast(msg) {
    var t = $('[data-toast]');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('is-open');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('is-open'); }, 2600);
  }

  /* ── Events (delegated) ──────────────────────────────────────────────────── */
  function bindEvents() {
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-add],[data-buynow],[data-notify],[data-like],[data-filter],[data-soon],[data-remove],[data-cart-open],[data-drawer-close],[data-drawer-backdrop],[data-checkout],[data-pay-button],[data-pay-back],[data-modal-close],[data-modal-backdrop]');
      if (!t) return;

      if (t.hasAttribute('data-like'))       { e.preventDefault(); toggleLike(t.getAttribute('data-like')); }
      else if (t.hasAttribute('data-soon'))  { e.preventDefault(); toast(t.getAttribute('data-soon') + ' arrives at launch'); }
      else if (t.hasAttribute('data-add'))   { addToCart(t.getAttribute('data-add')); }
      else if (t.hasAttribute('data-buynow')){
        var bp = findProduct(t.getAttribute('data-buynow'));
        if (bp && bp.variant_id) goToShopifyCheckout([{ variant_id: String(bp.variant_id), qty: 1 }]);
        else toast('This piece is unavailable for checkout');
      }
      else if (t.hasAttribute('data-notify')){ toast('We will notify you at launch'); }
      else if (t.hasAttribute('data-filter')){ state.filter = t.getAttribute('data-filter'); renderFilters(); renderCatalog(); }
      else if (t.hasAttribute('data-remove')) { removeLine(t.getAttribute('data-remove')); }
      else if (t.hasAttribute('data-cart-open')) { e.preventDefault(); openDrawer(); }
      else if (t.hasAttribute('data-drawer-close') || t.hasAttribute('data-drawer-backdrop')) { closeDrawer(); }
      else if (t.hasAttribute('data-checkout')) { goToShopifyCheckout(); }
      else if (t.hasAttribute('data-modal-close') || t.hasAttribute('data-modal-backdrop')) { closeCheckout(); }
    });

    var form = $('[data-checkout-form]');
    if (form) form.addEventListener('submit', submitCheckoutDetails);

    var newsletter = $('[data-newsletter-form]');
    if (newsletter) newsletter.addEventListener('submit', function (e) {
      e.preventDefault();
      toast('You’re on the list — see you at launch');
      newsletter.reset();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeDrawer(); closeCheckout(); }
    });

    window.addEventListener('hashchange', route);
  }

  /* ── Craft timeline (scroll-reveal) ──────────────────────────────────────────
   * Only the step crossing the viewport's centre band stays open; as you scroll
   * further, the previous step leaves the band and collapses again. Hover also
   * opens a step (handled in CSS). Mirrors the live TAAR craft-story section. */
  function initCraftTimeline() {
    var steps = document.querySelectorAll('[data-cs-step]');
    if (!steps.length) return;

    var reduce = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* No reveal support (or reduced motion): show everything open. */
    if (reduce || !('IntersectionObserver' in window)) {
      steps.forEach(function (s) { s.classList.add('is-open'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        entry.target.classList.toggle('is-open', entry.isIntersecting);
      });
    }, { threshold: 0, rootMargin: '-46% 0px -46% 0px' });

    steps.forEach(function (s) { io.observe(s); });
  }

  /* ── Boot ────────────────────────────────────────────────────────────────── */
  function init() {
    bindEvents();
    initCraftTimeline();
    loadProducts().then(function () {
      renderFilters();
      renderCatalog();
      initCatalogCarousel();
      syncCart();
      syncLikes();
      route();
      refreshAvailability();                    // live stock: first pass
      setInterval(refreshAvailability, 60000);  // …then re-check every 60s
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
